import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { publicApi, getImageUrl } from '../lib/api';

const noDownload = {
  draggable: false,
  onContextMenu: (e) => e.preventDefault(),
};

// Convert a YouTube or Vimeo share URL into an embeddable player URL
const toEmbedUrl = (url) => {
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return url;
};

const EnquireCta = () => (
  <div className="text-center">
    <Link to="/quote">
      <Button
        className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-10 py-6 text-base transition-transform duration-300 hover:scale-105"
        data-testid="enquire-about-event-btn"
      >
        Enquire About Your Event
      </Button>
    </Link>
  </div>
);

const EventGallery = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await publicApi.getEvent(slug);
        setEvent(data);
      } catch (e) {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const photos = event?.photos || [];
  const allVideos = event?.videos || [];
  const videos = allVideos.filter((v) => v.orientation !== 'vertical');
  const reels = allVideos.filter((v) => v.orientation === 'vertical');
  const embeds = event?.embeds || [];

  const move = useCallback(
    (dir) => setLightboxIdx((prev) => (prev === null ? null : (prev + dir + photos.length) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowRight') move(1);
      if (e.key === 'ArrowLeft') move(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, move]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1F1A17] pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#C5A880] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#1F1A17] pt-20 flex items-center justify-center px-4">
        <Card className="bg-[#2A2420] border-[#C5A880]/20 max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-[#EADBC8] mb-4">Event not found</p>
            <Link to="/portfolio">
              <Button className="bg-[#C5A880] text-[#1F1A17] hover:bg-[#D4BC95]">Back to Our Work</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F1A17] pt-20">
      <Seo
        title={`${event.title} — Our Work`}
        description={event.description || `${event.event_type || 'Event'} by Mayden Films${event.location ? ` at ${event.location}` : ''}.`}
        path={`/our-work/${event.slug}`}
        image={event.hero_image ? getImageUrl(event.hero_image, 'full') : undefined}
      />

      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          {event.hero_image && (
            <img src={getImageUrl(event.hero_image, 'full')} alt="" aria-hidden="true" {...noDownload} style={{ objectPosition: `${event.hero_focal_x ?? 50}% ${event.hero_focal_y ?? 50}%` }} className="w-full h-full object-cover opacity-20 select-none" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F1A17]/80 via-[#1F1A17]/60 to-[#1F1A17]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-[#EADBC8]/70 hover:text-[#C5A880] mb-6 transition-colors" data-testid="back-to-events">
            <ArrowLeft size={18} className="mr-2" />
            Back to Our Work
          </Link>
          <h1 className="text-4xl md:text-6xl font-bold text-[#EADBC8] mb-4">{event.title}</h1>
          <div className="flex flex-wrap gap-4 text-gray-300 items-center">
            {event.event_type && (
              <span className="bg-[#C5A880] text-[#1F1A17] text-xs font-bold px-3 py-1 rounded-full uppercase">{event.event_type}</span>
            )}
            {event.event_date && (
              <span className="flex items-center gap-2"><Calendar size={16} className="text-[#C5A880]" />{event.event_date}</span>
            )}
            {event.location && (
              <span className="flex items-center gap-2"><MapPin size={16} className="text-[#C5A880]" />{event.location}</span>
            )}
          </div>
          {event.description && <p className="text-gray-300 text-lg mt-6 max-w-3xl">{event.description}</p>}
        </div>
      </section>

      {/* Highlight video(s) */}
      {videos.length > 0 && (
        <section className="py-12 bg-[#1A1612]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">
              {videos.length === 1 ? 'Highlight Video' : 'Highlight Videos'}
            </h2>
            <div className={`grid gap-6 ${videos.length === 1 ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
              {videos.map((video, idx) => (
                <div key={video.public_id || idx} className="bg-[#2A2420] rounded-xl overflow-hidden border border-[#C5A880]/20">
                  <video
                    src={video.url}
                    className="w-full aspect-video"
                    controls
                    controlsList="nodownload noplaybackrate"
                    disablePictureInPicture
                    preload="metadata"
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid={`event-video-${idx}`}
                  />
                  {video.title && <p className="text-[#EADBC8] text-sm p-3 truncate">{video.title}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reels (vertical) */}
      {reels.length > 0 && (
        <section className="py-12 bg-[#1F1A17]" data-testid="event-reels">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">Reels</h2>
            <div className="flex gap-4 overflow-x-auto pb-2 justify-start sm:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {reels.map((reel, idx) => (
                <div key={reel.public_id || idx} className="flex-shrink-0 w-[240px] bg-[#2A2420] rounded-xl overflow-hidden border border-[#C5A880]/20">
                  <video
                    src={reel.url}
                    className="w-full aspect-[9/16] bg-black"
                    controls
                    controlsList="nodownload noplaybackrate"
                    disablePictureInPicture
                    preload="metadata"
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid={`event-reel-${idx}`}
                  />
                  {reel.title && <p className="text-[#EADBC8] text-xs p-2.5 truncate">{reel.title}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* YouTube / Vimeo embeds */}
      {embeds.length > 0 && (
        <section className="py-12 bg-[#1A1612]" data-testid="event-embeds">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">Featured Films</h2>
            <div className={`grid gap-6 ${embeds.length === 1 ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
              {embeds.map((em, idx) => (
                <div key={idx} className="bg-[#2A2420] rounded-xl overflow-hidden border border-[#C5A880]/20">
                  <div className="aspect-video">
                    <iframe
                      src={toEmbedUrl(em.url)}
                      title={em.title || `Film ${idx + 1}`}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid={`event-embed-${idx}`}
                    />
                  </div>
                  {em.title && <p className="text-[#EADBC8] text-sm p-3 truncate">{em.title}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <section className="py-12 bg-[#1F1A17] min-h-[30vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {photos.length > 0 && (
            <div className="text-center mb-8">
              <p className="text-[#C5A880] text-sm font-semibold tracking-wider uppercase">
                {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mt-2">Gallery</h2>
            </div>
          )}
          {photos.length === 0 && videos.length === 0 && reels.length === 0 && embeds.length === 0 ? (
            <p className="text-gray-300 text-center">Photos coming soon.</p>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((photo, idx) => (
                <button
                  key={photo.public_id || idx}
                  onClick={() => setLightboxIdx(idx)}
                  className="aspect-square overflow-hidden rounded-lg group bg-[#2A2420] relative"
                  data-testid={`gallery-photo-${idx}`}
                >
                  <img
                    src={getImageUrl(photo.url, 'thumb')}
                    alt={photo.alt || `${event.title} — ${event.event_type || 'event'} photo ${idx + 1}`}
                    {...noDownload}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 select-none"
                    loading="lazy"
                  />
                  {photo.caption && (
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#1F1A17]/90 to-transparent text-[#EADBC8] text-[11px] p-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">{photo.caption}</span>
                  )}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* Enquire CTA */}
      <section className="py-20 bg-gradient-to-br from-[#2A2420] via-[#1F1A17] to-[#2A2420]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-5">Love this? Let’s create yours.</h2>
          <p className="text-[#EADBC8]/70 text-lg mb-8">Tell me the date, venue and celebration, and I’ll confirm availability.</p>
          <EnquireCta />
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div className="fixed inset-0 z-50 bg-[#1F1A17]/95 flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)}>
          <button onClick={() => setLightboxIdx(null)} className="absolute top-4 right-4 text-[#EADBC8] hover:text-[#C5A880] z-10" data-testid="lightbox-close">
            <X size={32} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); move(-1); }} className="absolute left-4 text-[#EADBC8] hover:text-[#C5A880] text-5xl z-10" aria-label="Previous">‹</button>
          <img
            src={getImageUrl(photos[lightboxIdx].url, 'full')}
            alt={`${event.title} — photo ${lightboxIdx + 1}`}
            {...noDownload}
            className="max-h-[90vh] max-w-[90vw] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
          />
          <button onClick={(e) => { e.stopPropagation(); move(1); }} className="absolute right-4 text-[#EADBC8] hover:text-[#C5A880] text-5xl z-10" aria-label="Next">›</button>
          <p className="absolute bottom-4 text-gray-400 text-sm">{lightboxIdx + 1} / {photos.length}</p>
        </div>
      )}
    </div>
  );
};

export default EventGallery;
